import React from "react";
import { convertGraphQLErrorsToSubmissionErrors } from "@hex-insights/app-modules";
import {
	Button,
	ButtonProps,
	ButtonWithConfirmation,
	Column,
	Heading,
	If,
	Modal,
	ModalProps,
	RequiredKeys,
	Row,
} from "@hex-insights/core";
import {
	anyFieldChanged,
	FormSubmitFunction,
	submissionFailure,
	submissionSuccess,
	SubmitButton,
} from "@hex-insights/forms";
import { Icon } from "../../../../../Components/Icon";
import {
	AcademicTerm,
	graphTypeNames,
	Student,
	StudentEnrollmentsListQuery,
	StudentNameFragment,
	useSetStudentEnrollmentMutation,
} from "../../../../../Utilities";
import { AcademicTermFormat } from "../../../../Format";
import { StudentEnrollmentForm } from "../../../../Forms";
import styles from "./styles.module.css";

export type StudentEnrollmentFormControllerProps = Pick<ButtonProps, "variant" | "size" | "children"> &
	Omit<StudentEnrollmentFormModalProps, "ifRef" | "onClose">;

export function StudentEnrollmentFormController({
	variant = "link",
	size = "small",
	children = (
		<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
			<Icon.PencilSimple size={16} /> Edit Enrollment
		</Row>
	),
	...props
}: StudentEnrollmentFormControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant={variant} size={size} onClick={openModal}>
				{children}
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentEnrollmentFormModal {...props} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

type StudentEnrollmentFormModalProps = {
	student: Pick<Student, "id" | "graduationYear"> & StudentNameFragment;
	academicTerm: Pick<AcademicTerm, "id" | "name">;
	academicTermEnrollment:
		| StudentEnrollmentsListQuery["studentAcademicTermEnrollmentConnection"]["edges"][0]["node"]
		| null;
	homeRoomSectionEnrollmentEdges: StudentEnrollmentsListQuery["studentHomeRoomSectionEnrollmentConnection"]["edges"];
	courseSectionEnrollmentEdges: StudentEnrollmentsListQuery["studentCourseSectionEnrollmentConnection"]["edges"];
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose">, "onClose">;

function StudentEnrollmentFormModal({
	student,
	academicTerm,
	academicTermEnrollment,
	homeRoomSectionEnrollmentEdges,
	courseSectionEnrollmentEdges,
	ifRef,
	onClose,
}: StudentEnrollmentFormModalProps) {
	const isEnrolled = academicTermEnrollment !== null;

	const homeRoomSectionEnrollment = homeRoomSectionEnrollmentEdges[0]?.node ?? null;
	const initialFormValues = React.useMemo(
		() =>
			StudentEnrollmentForm.toEnrollmentOnExistingFormValues(
				student,
				academicTermEnrollment,
				homeRoomSectionEnrollment,
				courseSectionEnrollmentEdges,
			),
		[student, academicTermEnrollment, homeRoomSectionEnrollment, courseSectionEnrollmentEdges],
	);
	const formState = StudentEnrollmentForm.useEnrollmentOnExistingFormState({ initialFormValues });

	const [setStudentEnrollment] = useSetStudentEnrollmentMutation();
	const onSubmit = React.useCallback<FormSubmitFunction<StudentEnrollmentForm.EnrollmentOnExistingFormValues>>(
		async (formState) => {
			if (!formState.formValues.graduationYear) {
				return submissionFailure([]);
			}

			const { errors } = await setStudentEnrollment({
				variables: {
					studentID: student.id,
					academicTermID: academicTerm.id,
					lateStartDate: formState.formValues.lateStartDate,
					earlyEndDate: formState.formValues.earlyEndDate,
					graduationYear: formState.formValues.graduationYear,
					homeRoomSectionID: formState.formValues.homeRoomSectionID || null, // clear with existing value sets to empty string instead of null
					courseSectionIDs: formState.formValues.courseSectionIDs,
				},
				update: (cache, { data }) => {
					if (!(data && data.setStudentEnrollment)) {
						return;
					}

					cache.evict({ id: cache.identify({ id: student.id, __typename: graphTypeNames.Student }) });
					cache.evict({ id: "ROOT_QUERY", fieldName: "studentAcademicTermEnrollmentConnection" });
					cache.evict({ id: "ROOT_QUERY", fieldName: "studentHomeRoomSectionEnrollmentConnection" });
					cache.evict({ id: "ROOT_QUERY", fieldName: "studentCourseSectionEnrollmentConnection" });
					cache.evict({ id: "ROOT_QUERY", fieldName: "homeRoomSectionConnection" });
					cache.evict({ id: "ROOT_QUERY", fieldName: "courseSectionConnection" });
					cache.evict({ id: "ROOT_QUERY", fieldName: "studentConnection" });
				},
			});

			if (errors) {
				return submissionFailure(convertGraphQLErrorsToSubmissionErrors(errors));
			}
			return submissionSuccess();
		},
		[setStudentEnrollment, student.id, academicTerm.id],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={styles["enrollment-edit-modal"]}>
			<Modal.Header>
				<Heading.H2 noMargin>Enrollment</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1.25rem">
					<Column>
						<span style={{ fontSize: "0.875rem", color: "var(--neutral-colors--secondary)" }}>Enrolling in</span>
						<span style={{ fontSize: "1.5rem" }}>{AcademicTermFormat.name(academicTerm)}</span>
					</Column>

					<StudentEnrollmentForm.OnExisting
						academicTermID={academicTerm.id}
						studentID={student.id}
						formState={formState}
						onSubmit={onSubmit}
						onSuccess={onClose}
						columnStyle={{ width: "100%" }}
					/>
				</Column>
			</Modal.Body>
			<If condition={!isEnrolled || anyFieldChanged(formState)}>
				<Modal.Footer>
					<Row justify="space-between" align="center">
						<ButtonWithConfirmation
							variant="link"
							onClick={onClose}
							confirmationContent="This will clear any changes you have made."
						>
							Cancel
						</ButtonWithConfirmation>

						<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
							{isEnrolled ? "Save Changes" : "Enroll Student"}
						</SubmitButton>
					</Row>
				</Modal.Footer>
			</If>
		</Modal>
	);
}
